import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _excluded = ["locked", "value", "readOnly", "error", "label", "errorText", "disabled", "width", "isOpen", "ariaLabel", "required", "bodyConfig", "color", "typescale", "minWidth", "maxWidth", "hoverState", "calculateInputPadding", "labelMarginBottom", "overflowEllipsis", "readOnlyBorders", "focusState", "placeholder", "id", "renderErrorIcon", "iconConfig", "type", "inputHeight", "dateFormat", "CreditCardIcon", "Tooltip", "tooltipTitle", "tooltipContent", "helperTextPlacement", "helperText", "success", "successText", "Body", "Micro", "taxIDFormat", "cardType", "onClickInlineAction", "maxLength", "className", "inputId", "mask", "renderInputIcon", "errorLabelSpacing", "inverted", "caretColor"];

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12;

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cuid from 'cuid';
import { HelperText, ErrorText, Label, showError } from '@vds-core/form-elements';
import Icon from '@vds-core/icons';
import { calculateRem } from '@uie/javascript-style-helpers';
import { BodyConfig, TypographyConfig, Micro, Body } from '@vds-core/typography';
import { colors, calculateSpacing } from '@vds-core/theme';
import { creditCardOnChange, dateOnChange, _ignoreAlpha, _calculateCCMaxLength, _calculateDateMaxLength, determineMaxLength, _maskValue, _formatToPhone, stickyPlaceholderHTML
/* _taxIDOnChange, */
} from './util/InputFormatting';
import { withVDSManager } from '@vds-core/utilities';

function _calculateInputPadding(error, readOnly, locked, type, readOnlyBorders) {
  if (type === 'calendar') {
    if ((readOnly || locked) && !readOnlyBorders) {
      return calculateRem(12, 16, 12, 32);
    } else {
      return calculateRem(12, 16, 12, 50);
    }
  } else {
    if ((readOnly || locked) && !readOnlyBorders) {
      return 0;
    } else {
      return calculateRem(12, 16);
    }
  }
}

var propTypes = {
  /**
   * If provided, the Input will be rendered in the Disabled State.
   */
  disabled: PropTypes.bool,

  /**
   * If provided, will provide the defaultValue for the input field.
   */
  defaultValue: PropTypes.any,

  /**
   * If provided, the Input will be rendered in the ReadOnly State.
   */
  readOnly: PropTypes.bool,

  /**
   * The prop for the HTML name Attribute. This is used to specify a name for an input element. It is used to reference the form-data after submitting the form or to reference the element.
   */
  name: PropTypes.string,

  /**
   *  Determines what event this component will check for an error, options are 'onBlur' or 'onChange'.
   */
  errorEvent: PropTypes.oneOf(['blur', 'change', 'blurAndChange']),

  /**
   * Boolean or Function that returns a boolean value that determines if component should show the error state/error message. Function receives the 'event' object  on input change.
   * @note If this is provided, you are responsible for controlling all errors for this component. Otherwise, if the Input is required, this component will throw an error if the component is blurred and does not contain any input.
   */
  error: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),

  /**
   * If provided, an in the error state, the Input will render error text.
   */
  errorText: PropTypes.string,

  /**
   * If provided, will provide the context for the 'label' on the input field.
   */
  label: PropTypes.string,

  /**
   * If provided, will override the default sizing widths for the small and large datepicker input.
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * If provided, this is a callback function for when input text is changed.
   */
  onChange: PropTypes.func,

  /**
   * @ignore
   */
  isOpen: PropTypes.bool,

  /**
   * If provided, used as string that labels the element for accessibility.
   */
  ariaLabel: PropTypes.string,

  /**
   * @deprecated readOnly
   */
  locked: PropTypes.bool,

  /**
   * When true, will add the required attribute to the input element, if false, a string of 'Optional' next to the label
   */
  required: PropTypes.bool,

  /**
   * @ignore - config for passing through icons
   */
  iconConfig: PropTypes.object,

  /**
   * @ignore - config for passing through other colors
   */
  color: PropTypes.object,

  /**
   * @ignore - config for passing though typography
   */
  bodyConfig: PropTypes.object,

  /**
   * @ignore - passes through typescale via sting if there's more than one option [example 'VDS' instead of 'MVP']
   */
  typescale: PropTypes.string,

  /**
   * @ignore - adds max-width to input field
   */
  maxWidth: PropTypes.string,

  /**
   * @ignore - adds min-width to input field
   */
  minWidth: PropTypes.string,

  /**
   * @ignore - adds hover state to input field
   */
  hoverState: PropTypes.bool,

  /**
   * @ignore - adds or removes focus states
   */
  focusState: PropTypes.bool,

  /**
   * @ignore - number representing height of input field
   */
  inputHeight: PropTypes.string,

  /**
   * @ignore - padding on the inside of the input field
   */
  calculateInputPadding: PropTypes.func,

  /**
   * @ignore defines the space between the select and the error text below
   */
  errorLabelSpacing: PropTypes.string,

  /**
   * @ignore - spacing between the label and the input field
   */
  labelMarginBottom: PropTypes.string,

  /**
   * @ignore - defines whether text trancates with ellipsis or not
   */
  overflowEllipsis: PropTypes.bool,

  /**
   * @ignore
   * spacing between the label and the input field
   */
  value: PropTypes.string,

  /**
   * @ignore - adds the ability to use borders when in ready only state (default is borderless)
   */
  readOnlyBorders: PropTypes.bool,

  /**
   * @ignore renders Error icon if true
   */
  renderErrorIcon: PropTypes.bool,

  /**
   * @ignore string representing the type of input
   */
  type: PropTypes.string,

  /**
   * @ignore If passed, new TextLink component will render for inlineAction
   */
  TextLink: PropTypes.func,

  /**
   * @ignore If passed, new Body component will render
   */
  Body: PropTypes.func,

  /**
   * @ignore If passed, new Micro component will render
   */
  Micro: PropTypes.func,

  /**
   * Allows a unique ID to be passed to the component.
   */
  id: PropTypes.string,

  /**
   * Allows a unique ID to be passed to the input element.
   */
  inputId: PropTypes.string,

  /**
   * @ignore If passed, the CreditCardIcon will render
   */
  CreditCardIcon: PropTypes.func,

  /**
   * @ignore If passed, the Tooltip will render
   */
  Tooltip: PropTypes.func,

  /**
   *  If passed, sets Max character length for input field
   */
  maxLength: PropTypes.number,

  /**
   * @ignore If passed, an icon like calendar icon will render
   */
  renderInputIcon: PropTypes.func,

  /**
   * If provided, the Input will be rendered the inverted state.
   */
  inverted: PropTypes.bool
};
var defaultProps = {
  disabled: false,
  error: undefined,
  errorEvent: 'blur',
  locked: false,
  readOnly: false,
  label: undefined,
  isOpen: false,
  inputHeight: calculateRem(42),
  required: true,
  color: colors,
  bodyConfig: BodyConfig,
  iconConfig: undefined,
  typescale: TypographyConfig.getTypescale(),
  maxWidth: undefined,
  minWidth: undefined,
  focusState: false,
  hoverState: false,
  errorLabelSpacing: calculateRem(8),
  labelMarginBottom: calculateRem(1),
  overflowEllipsis: false,
  calculateInputPadding: _calculateInputPadding,
  readOnlyBorders: false,
  Body: Body,
  Micro: Micro,
  renderErrorIcon: false,
  TextLink: undefined,
  type: undefined,
  CreditCardIcon: undefined,
  Tooltip: undefined,
  width: '100%',
  inverted: false //Deprecated
  // locked: false

};
var ComponentContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ComponentContainer",
  componentId: "sc-q2v02a-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  display: flex;\n  height: 100%;\n  flex-direction: ", ";\n\n  width: ", ";\n\n  max-width: ", ";\n  min-width: ", ";\n"])), function (_ref) {
  var helperTextPlacement = _ref.helperTextPlacement;
  return helperTextPlacement === 'right' ? 'row' : 'column';
}, function (_ref2) {
  var containerWidth = _ref2.containerWidth;
  return typeof containerWidth === 'number' ? calculateRem(containerWidth) : containerWidth;
}, function (_ref3) {
  var maxWidth = _ref3.maxWidth;
  return calculateRem(maxWidth);
}, function (_ref4) {
  var minWidth = _ref4.minWidth;
  return calculateRem(minWidth);
});
var StyledContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledContainer",
  componentId: "sc-q2v02a-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  display: flex;\n  flex: 1 1 auto;\n  position: relative;\n  flex-direction: column;\n  input[type='number'] {\n    -moz-appearance: textfield; /* Firefox */\n  }\n"])));
var InputContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "InputContainer",
  componentId: "sc-q2v02a-2"
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  position: relative;\n  justify-content: space-between;\n  height: ", ";\n  max-width: ", ";\n  min-width: ", ";\n  background-color: ", ";\n  box-sizing: border-box;\n  border: ", " solid\n    ", ";\n  border-bottom: ", " solid\n    ", ";\n  ", ";\n\n  ", ";\n  ", ";\n"])), function (_ref5) {
  var inputHeight = _ref5.inputHeight;
  return inputHeight;
}, function (_ref6) {
  var maxWidth = _ref6.maxWidth;
  return maxWidth && calculateRem(maxWidth);
}, function (_ref7) {
  var minWidth = _ref7.minWidth;
  return minWidth && calculateRem(minWidth);
}, function (_ref8) {
  var color = _ref8.color,
      inverted = _ref8.inverted;
  return inverted ? color.black : color.secondary;
}, calculateRem(1), function (_ref9) {
  var color = _ref9.color,
      error = _ref9.error,
      isOpen = _ref9.isOpen,
      disabled = _ref9.disabled,
      focused = _ref9.focused,
      inverted = _ref9.inverted,
      type = _ref9.type;
  return (isOpen || focused) && !inverted ? color.black : isOpen && type === 'calendar' && inverted && error ? color.coolGray10 : (isOpen || focused) && inverted ? color.white : error && !disabled ? color.error : inverted ? color.coolGray10 : color.coolGray3;
}, calculateRem(1), function (_ref10) {
  var color = _ref10.color,
      disabled = _ref10.disabled,
      inverted = _ref10.inverted;
  return disabled && !inverted ? color.coolGray3 : disabled && inverted ? color.coolGray10 : inverted ? color.white : color.black;
}, function (_ref11) {
  var readOnlyBorders = _ref11.readOnlyBorders,
      readOnly = _ref11.readOnly,
      locked = _ref11.locked,
      color = _ref11.color,
      inverted = _ref11.inverted;
  return (readOnly || locked) && "\n      border: ".concat(readOnlyBorders && !inverted ? "".concat(calculateRem(1), " solid ").concat(color.coolGray3) : readOnlyBorders && inverted ? "".concat(calculateRem(1), " solid ").concat(color.coolGray10) : 'none', ";\n  ");
}, function (_ref12) {
  var hoverState = _ref12.hoverState,
      readOnly = _ref12.readOnly,
      error = _ref12.error,
      success = _ref12.success,
      disabled = _ref12.disabled,
      color = _ref12.color,
      inverted = _ref12.inverted;
  return hoverState && "\n      &:hover {\n        border: ".concat(calculateRem(1), " solid ").concat((readOnly || disabled) && !inverted ? color.coolGray3 : (readOnly || disabled) && inverted ? color.coolGray10 : inverted ? color.white : color.black, ";\n      border-bottom: ").concat(calculateRem(1), " solid ").concat((readOnly || disabled) && !inverted ? color.coolGray3 : (readOnly || disabled) && inverted ? color.coolGray10 : error ? color.error : success ? color.green : inverted ? color.white : color.black, ";\n  }\n  ");
}, function (_ref13) {
  var error = _ref13.error,
      success = _ref13.success,
      disabled = _ref13.disabled,
      color = _ref13.color,
      helperTextPlacement = _ref13.helperTextPlacement,
      errorText = _ref13.errorText,
      successText = _ref13.successText,
      label = _ref13.label,
      readOnly = _ref13.readOnly;
  return (error || success) && !disabled && !readOnly && "\n    &::before {\n      background:  ".concat(error ? color.orange : color.green, ";\n      box-shadow: none;\n      content: '';\n      height: ").concat(calculateRem(4), ";\n      left: ").concat(-1, "px;\n      position: absolute;\n      bottom: ").concat(-1, "px;\n      width: calc(100% + ").concat(calculateRem(2), ");\n      z-index: 1;\n    }\n  ");
});
var StickyPlaceholder = /*#__PURE__*/styled.div.withConfig({
  displayName: "StickyPlaceholder",
  componentId: "sc-q2v02a-3"
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  color: ", ";\n  font-family: ", ";\n  font-size: ", ";\n  letter-spacing: ", ";\n  line-height: ", ";\n  margin-left: ", ";\n  pointer-events: none;\n  position: absolute;\n  top: 50%;\n  transform: translateY(-50%);\n\n  span {\n    color: transparent;\n  }\n  .slash {\n    color: ", ";\n  }\n"])), function (_ref14) {
  var color = _ref14.color,
      disabled = _ref14.disabled;
  return disabled ? color.coolGray3 : color.coolGray6;
}, function (_ref15) {
  var body = _ref15.body;
  return body.fontFamily;
}, function (_ref16) {
  var body = _ref16.body;
  return calculateRem(body.fontSize);
}, function (_ref17) {
  var body = _ref17.body;
  return calculateRem(body.letterSpacing);
}, function (_ref18) {
  var body = _ref18.body;
  return calculateRem(body.lineHeight);
}, calculateSpacing('3X'), function (_ref19) {
  var color = _ref19.color,
      disabled = _ref19.disabled;
  return disabled ? color.coolGray3 : color.black;
});
var StyledInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "StyledInput",
  componentId: "sc-q2v02a-4"
})(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  flex: 1;\n  display: flex;\n  width: 100%;\n  border: none;\n  box-sizing: border-box;\n  background-color: transparent;\n  caret-color: ", ";\n  color: ", ";\n  font-family: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  letter-spacing: ", ";\n  margin: 0;\n  ::-ms-clear,\n  ::-ms-reveal {\n    display: none;\n  }\n  &::-webkit-outer-spin-button,\n  &::-webkit-inner-spin-button {\n    /* display: none; <- Crashes Chrome on hover */\n    -webkit-appearance: none;\n    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */\n  }\n  &[disabled] {\n    -webkit-text-fill-color: ", ";\n  }\n\n  ::placeholder {\n    position: relative;\n    color: ", " !important;\n    top: 0px;\n  }\n\n  &:focus {\n    outline: none;\n  }\n  padding: ", ";\n"])), function (_ref20) {
  var color = _ref20.color,
      inverted = _ref20.inverted,
      caretColor = _ref20.caretColor;
  return caretColor ? caretColor : inverted ? color.white : color.black;
}, function (_ref21) {
  var color = _ref21.color,
      disabled = _ref21.disabled,
      inverted = _ref21.inverted;
  return disabled && !inverted ? color.coolGray3 : disabled && inverted ? color.coolGray10 : inverted ? color.white : color.black;
}, function (_ref22) {
  var body = _ref22.body;
  return body.fontFamily;
}, function (_ref23) {
  var body = _ref23.body;
  return calculateRem(body.fontSize);
}, function (_ref24) {
  var body = _ref24.body;
  return calculateRem(body.lineHeight);
}, function (_ref25) {
  var body = _ref25.body,
      masked = _ref25.masked;
  return masked ? calculateSpacing('1X') : calculateRem(body.letterSpacing);
}, function (_ref26) {
  var inverted = _ref26.inverted,
      color = _ref26.color;
  return !inverted ? color.coolGray3 : color.coolGray10;
}, function (_ref27) {
  var color = _ref27.color,
      inverted = _ref27.inverted;
  return inverted ? color.coolGray10 : color.coolGray3;
}, function (_ref28) {
  var calculateInputPadding = _ref28.calculateInputPadding,
      error = _ref28.error,
      readOnly = _ref28.readOnly,
      locked = _ref28.locked,
      trueType = _ref28.trueType,
      readOnlyBorders = _ref28.readOnlyBorders;
  return calculateInputPadding(error, readOnly, locked, trueType, readOnlyBorders);
});
var ActionWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "ActionWrapper",
  componentId: "sc-q2v02a-5"
})(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  display: inline-flex;\n  height: ", ";\n  padding: ", ";\n"])), calculateRem(20), calculateRem(12, 12, 12, 0));
var IconContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "IconContainer",
  componentId: "sc-q2v02a-6"
})(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n  display: inline-flex;\n  align-items: center;\n  padding-right: ", ";\n  outline: none;\n  pointer-events: none;\n"])), function (_ref29) {
  var renderPadding = _ref29.renderPadding;
  return renderPadding && calculateSpacing('2X');
});
var CalendarIconWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "CalendarIconWrapper",
  componentId: "sc-q2v02a-7"
})(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n  display: inline-flex;\n  top: ", ";\n  left: ", ";\n  position: absolute;\n  z-index: 1;\n"])), function (_ref30) {
  var typescale = _ref30.typescale;
  return typescale === 'VDS' ? '12px' : '11px';
}, function (_ref31) {
  var readOnly = _ref31.readOnly,
      readOnlyBorders = _ref31.readOnlyBorders;
  return readOnly && !readOnlyBorders ? 0 : readOnlyBorders ? '12px' : '16px';
});
var InputWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputWrapper",
  componentId: "sc-q2v02a-8"
})(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n  flex: 1;\n  position: relative;\n  width: 100%;\n"])));
var FlexedRowContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "FlexedRowContainer",
  componentId: "sc-q2v02a-9"
})(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: row;\n  position: relative;\n"])));
var FlexedColContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "FlexedColContainer",
  componentId: "sc-q2v02a-10"
})(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n  display: flex;\n  flex: 1 1 auto;\n  flex-direction: column;\n  position: relative;\n  width: ", ";\n"])), function (_ref32) {
  var helperText = _ref32.helperText,
      helperTextPlacement = _ref32.helperTextPlacement;
  return helperText && helperTextPlacement === 'right' ? '50%' : '100%';
});
var CardIconContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "CardIconContainer",
  componentId: "sc-q2v02a-11"
})(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n  display: inline-flex;\n  padding: ", ";\n  width: ", ";\n  height: ", ";\n"])), calculateRem(12, 0, 12, 12), calculateRem(32), calculateRem(20));

var InputField = /*#__PURE__*/function (_React$Component) {
  _inherits(InputField, _React$Component);

  var _super = _createSuper(InputField);

  function InputField(props) {
    var _this2;

    _classCallCheck(this, InputField);

    _this2 = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this2), "componentDidUpdate", function (prevProps, prevState) {
      var _this2$props = _this2.props,
          emptyError = _this2$props.emptyError,
          required = _this2$props.required,
          value = _this2$props.value,
          error = _this2$props.error,
          type = _this2$props.type;

      if (prevProps.value !== value && _this2.isControlled) {
        _this2.setState({
          value: value
        });
      }

      if (typeof error === 'boolean' && prevProps.error !== error) {
        _this2.setState({
          error: error
        });
      } // datepicker passes an error prop regardless, so need to check for non datepicker inputfields if the error is undefined
      // all empty error handling for datepicker is handled within datepicker


      if (type !== 'calendar' && error === undefined) {
        if (prevProps.emptyError !== emptyError) {
          _this2.setState({
            emptyError: emptyError
          });
        }

        if (prevProps.required !== _this2.props.required) {
          _this2.setState({
            emptyError: false
          });
        }
      }

      if (type === 'calendar') {
        if (prevProps.emptyError !== emptyError) {
          _this2.setState({
            emptyError: emptyError
          });
        }

        if (prevProps.required !== _this2.props.required) {
          _this2.setState({
            emptyError: false
          });
        }
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_onClick", function (e) {
      var _this2$props2 = _this2.props,
          onClickInlineAction = _this2$props2.onClickInlineAction,
          type = _this2$props2.type;
      var hidden = _this2.state.hidden;
      onClickInlineAction && onClickInlineAction(e); // if (this.props.type === 'taxID') {
      //   let clean = this.state.formattedValue.replace(/\D/g, '');
      //   if (!this.state.hidden) {
      //     this.setState({ value: clean });
      //   } else {
      //     this.setState({ value: this.state.formattedValue });
      //   }

      if (type === 'password') {
        if (hidden) {
          _this2.setState({
            hidden: false
          });

          setTimeout(function () {
            _this2.setState({
              hidden: true
            });
          }, 10000);
        } else {
          _this2.setState({
            hidden: true
          });
        }
      } // }

    });

    _defineProperty(_assertThisInitialized(_this2), "_onKeyDown", function (e) {
      var _this2$props3 = _this2.props,
          onClickInlineAction = _this2$props3.onClickInlineAction,
          type = _this2$props3.type;
      var hidden = _this2.state.hidden;

      if (e.keyCode === 13 || e.keyCode === 32) {
        onClickInlineAction && onClickInlineAction(e);

        if (type === 'password') {
          if (hidden) {
            _this2.setState({
              hidden: false
            });

            setTimeout(function () {
              _this2.setState({
                hidden: true
              });
            }, 10000);
          } else {
            _this2.setState({
              hidden: true
            });
          }
        }
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_renderIcon", function (error) {
      var _this2$props4 = _this2.props,
          type = _this2$props4.type,
          iconConfig = _this2$props4.iconConfig,
          success = _this2$props4.success,
          color = _this2$props4.color,
          mask = _this2$props4.mask,
          inverted = _this2$props4.inverted;
      var renderPadding = type === 'inlineAction' ||
      /* type === 'taxID' || */
      type === 'password' && _this2.state.value.length > 0 && mask === 'toggle';
      return /*#__PURE__*/React.createElement(IconContainer, {
        tabIndex: -1,
        renderPadding: renderPadding
      }, /*#__PURE__*/React.createElement(Icon, {
        tabIndex: -1,
        hasStroke: true,
        data: iconConfig,
        size: "medium",
        name: error ? 'error' : 'success',
        color: inverted ? color.white : color.black,
        ariaHidden: true
      }));
    });

    _defineProperty(_assertThisInitialized(_this2), "_renderInlineAction", function () {
      var _this2$props5 = _this2.props,
          type = _this2$props5.type,
          disabled = _this2$props5.disabled,
          readOnly = _this2$props5.readOnly,
          inverted = _this2$props5.inverted,
          inlineActionButtonLabel = _this2$props5.inlineActionButtonLabel,
          showPasswordButtonLabel = _this2$props5.showPasswordButtonLabel,
          hidePasswordButtonLabel = _this2$props5.hidePasswordButtonLabel,
          TextLink = _this2$props5.TextLink;
      var inlineActionText, inlineActionAriaLabel;

      if (type === 'password') {
        if (_this2.state.hidden) {
          inlineActionText = showPasswordButtonLabel;
          inlineActionAriaLabel = "".concat(showPasswordButtonLabel, " password");
        } else {
          inlineActionText = hidePasswordButtonLabel;
          inlineActionAriaLabel = "".concat(hidePasswordButtonLabel, " password");
        }
      } else {
        inlineActionText = inlineActionButtonLabel;
        inlineActionAriaLabel = inlineActionButtonLabel;
      } // if (type === 'taxID') {
      //   if (this.state.hidden) {
      //     inlineActionText = showPasswordButtonLabel;
      //     inlineActionAriaLabel = `${showPasswordButtonLabel} tax ID number`;
      //   } else {
      //     inlineActionText = hidePasswordButtonLabel;
      //     inlineActionAriaLabel = `${hidePasswordButtonLabel} tax ID number`;
      //   }
      // }


      return /*#__PURE__*/React.createElement(TextLink, {
        type: "standAlone",
        size: "large",
        viewport: "desktop",
        disabled: disabled || readOnly,
        onClick: _this2._onClick,
        ariaLabel: inlineActionAriaLabel,
        onKeyDown: _this2._onKeyDown,
        inverted: inverted
      }, inlineActionText);
    });

    _defineProperty(_assertThisInitialized(_this2), "_onChange", function (e) {
      var _this2$props6 = _this2.props,
          dateFormat = _this2$props6.dateFormat,
          type = _this2$props6.type,
          onChange = _this2$props6.onChange,
          taxIDFormat = _this2$props6.taxIDFormat,
          errorEvent = _this2$props6.errorEvent,
          error = _this2$props6.error,
          required = _this2$props6.required,
          emptyErrorProp = _this2$props6.emptyError;

      var _this = _assertThisInitialized(_this2);

      onChange && onChange(e); // check for error on change

      errorEvent && (errorEvent === 'change' || errorEvent === 'blurAndChange') && showError(e, _this);
      var obj;
      var fromOnChange = true;

      switch (type) {
        case 'creditCard':
          e.target.value = _ignoreAlpha(e);
          obj = creditCardOnChange(e, _this2.state.value, _this2.state.cardType, fromOnChange);
          if (required && obj.value.length > 0) _this2.setState({
            emptyError: false
          });
          return _this2.setState({
            value: obj.value,
            cardType: obj.cardType
          });

        case 'tel':
          obj = _formatToPhone(e, _this2.state.value, fromOnChange);
          _formatToPhone;
          if (required && obj.value.length > 0) _this2.setState({
            emptyError: false
          });
          return _this2.setState({
            value: obj.value
          });

        case 'date':
          e.target.value = _ignoreAlpha(e);
          obj = dateOnChange(e, _this2.state.value, dateFormat);
          if (required && obj.value.length > 0) _this2.setState({
            emptyError: false
          });
          return _this2.setState({
            value: obj.value
          });
        // case 'taxID':
        //   e.target.value = _ignoreAlpha(e);
        //   let targetValue = e.target.value;
        //   obj = _taxIDOnChange(
        //     e,
        //     this.state.value,
        //     this.state.hidden,
        //     taxIDFormat
        //   );
        //   return this.setState({
        //     formattedValue: obj.formattedValue,
        //     value: this.state.hidden ? targetValue : obj.value,
        //   });

        case 'securityCode':
          e.target.value = _ignoreAlpha(e);

        default:
          if (required && e.target.value.length > 0 && !emptyErrorProp) _this2.setState({
            emptyError: false
          });
          return !_this2.isControlled && _this2.setState({
            value: e.target.value
          });
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_determineInputType", function () {
      var _this2$props7 = _this2.props,
          type = _this2$props7.type,
          readOnly = _this2$props7.readOnly,
          locked = _this2$props7.locked;
      var hidden = _this2.state.hidden;

      if (
      /* type === 'taxID' ||  */
      type === 'password') {
        if (hidden || readOnly || locked) {
          return 'password';
        } else {
          return 'text';
        }
      } else if (type === 'securityCode') {
        return 'password';
      } else if (type === 'calendar' || type === 'date') {
        return 'text';
      }

      return type;
    });

    _defineProperty(_assertThisInitialized(_this2), "_determineValue", function () {
      var _this2$props8 = _this2.props,
          type = _this2$props8.type,
          readOnly = _this2$props8.readOnly,
          value = _this2$props8.value,
          defaultValue = _this2$props8.defaultValue;

      if (value || defaultValue) {
        return value;
      } else if (type === 'creditCard' && readOnly) {
        return _maskValue(_this2.state.value, _this2.state.cardType);
      }
      /* else if (type === 'taxID' && readOnly) {
      let clean = this.state.formattedValue.replace(/\D/g, '');
      return clean;
      } */
      else if (type === 'creditCard' && _this2.state.maskCC) {
        return _maskValue(_this2.state.value, _this2.state.cardType);
      } else {
        return _this2.state.value;
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_renderStickyPlaceholder", function (body) {
      var _this2$state = _this2.state,
          value = _this2$state.value,
          error = _this2$state.error;
      var _this2$props9 = _this2.props,
          color = _this2$props9.color,
          disabled = _this2$props9.disabled,
          dateFormat = _this2$props9.dateFormat,
          success = _this2$props9.success;
      return /*#__PURE__*/React.createElement(StickyPlaceholder, {
        success: success,
        error: error,
        body: body,
        color: color,
        disabled: disabled
      }, stickyPlaceholderHTML(dateFormat, value));
    });

    _defineProperty(_assertThisInitialized(_this2), "_onFocus", function (e) {
      var _this2$props10 = _this2.props,
          type = _this2$props10.type,
          readOnly = _this2$props10.readOnly;

      if (!readOnly) {
        if ((type === 'securityCode' || type === 'creditCard') && _this2.state.value.length > 0) {
          _this2.setState({
            value: '',
            cardType: 'placeholder',
            maskCC: false
          });
        }

        _this2.setState({
          focused: true
        });
      } // if (type === 'calendar') e.target.blur();

    });

    _defineProperty(_assertThisInitialized(_this2), "_onBlur", function (e) {
      var _this2$props11 = _this2.props,
          type = _this2$props11.type,
          readOnly = _this2$props11.readOnly,
          errorEvent = _this2$props11.errorEvent,
          error = _this2$props11.error,
          required = _this2$props11.required,
          emptyErrorProp = _this2$props11.emptyError;

      var _this = _assertThisInitialized(_this2);

      if (!readOnly) {
        if (type === 'creditCard' && _this2.state.value.length > 0) {
          _this2.setState({
            maskCC: true
          });
        }

        _this2.setState({
          focused: false
        });
      } // check for error on blur


      (errorEvent === 'blur' || errorEvent === 'blurAndChange') && showError(e, _this);

      if (required && !_this2.state.value && error === undefined && type !== 'calendar') {
        _this2.setState({
          emptyError: true
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_renderHelperText", function () {
      var _this2$props12 = _this2.props,
          helperText = _this2$props12.helperText,
          helperTextPlacement = _this2$props12.helperTextPlacement,
          bodyConfig = _this2$props12.bodyConfig,
          disabled = _this2$props12.disabled,
          color = _this2$props12.color,
          typescale = _this2$props12.typescale,
          Body = _this2$props12.Body,
          inverted = _this2$props12.inverted;
      return /*#__PURE__*/React.createElement(HelperText, {
        helperText: helperText,
        helperTextPlacement: helperTextPlacement,
        bodyConfig: bodyConfig,
        disabled: disabled,
        color: color,
        typescale: typescale,
        Body: Body,
        inverted: inverted
      });
    });

    _this2.state = {
      cardType: 'placeholder',
      value: _this2.props.value && _this2.props.type === 'text' ? _this2.props.value : _this2.props.defaultValue && _this2.props.type === 'text' ? _this2.props.defaultValue : '',
      hidden: true,
      focused: false,
      formattedValue: '',
      maskCC: false,
      error: typeof _this2.props.error === 'function' ? false : _this2.props.error,
      // If error passes a function, then the error state will initially be set to false
      emptyError: _this2.props.emptyError
    };
    return _this2;
  }

  _createClass(InputField, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.isControlled = this.props.type === 'text' && this.props.value !== undefined;
    }
  }, {
    key: "render",
    value: function render() {
      var _React$createElement;

      var _this$props = this.props,
          locked = _this$props.locked,
          value = _this$props.value,
          readOnly = _this$props.readOnly,
          errorProp = _this$props.error,
          label = _this$props.label,
          errorTextProp = _this$props.errorText,
          disabled = _this$props.disabled,
          width = _this$props.width,
          isOpen = _this$props.isOpen,
          ariaLabel = _this$props.ariaLabel,
          required = _this$props.required,
          bodyConfig = _this$props.bodyConfig,
          color = _this$props.color,
          typescale = _this$props.typescale,
          minWidth = _this$props.minWidth,
          maxWidth = _this$props.maxWidth,
          hoverState = _this$props.hoverState,
          calculateInputPadding = _this$props.calculateInputPadding,
          labelMarginBottom = _this$props.labelMarginBottom,
          overflowEllipsis = _this$props.overflowEllipsis,
          readOnlyBorders = _this$props.readOnlyBorders,
          focusState = _this$props.focusState,
          placeholder = _this$props.placeholder,
          id = _this$props.id,
          renderErrorIcon = _this$props.renderErrorIcon,
          iconConfig = _this$props.iconConfig,
          type = _this$props.type,
          inputHeight = _this$props.inputHeight,
          dateFormat = _this$props.dateFormat,
          CreditCardIcon = _this$props.CreditCardIcon,
          Tooltip = _this$props.Tooltip,
          tooltipTitle = _this$props.tooltipTitle,
          tooltipContent = _this$props.tooltipContent,
          helperTextPlacement = _this$props.helperTextPlacement,
          helperText = _this$props.helperText,
          success = _this$props.success,
          successText = _this$props.successText,
          Body = _this$props.Body,
          Micro = _this$props.Micro,
          taxIDFormat = _this$props.taxIDFormat,
          cardBrand = _this$props.cardType,
          onClickInlineAction = _this$props.onClickInlineAction,
          maxLength = _this$props.maxLength,
          className = _this$props.className,
          inputId = _this$props.inputId,
          mask = _this$props.mask,
          renderInputIcon = _this$props.renderInputIcon,
          errorLabelSpacing = _this$props.errorLabelSpacing,
          inverted = _this$props.inverted,
          caretColor = _this$props.caretColor,
          rest = _objectWithoutProperties(_this$props, _excluded);

      var _this$state = this.state,
          cardType = _this$state.cardType,
          focused = _this$state.focused,
          errorState = _this$state.error,
          emptyError = _this$state.emptyError; // Error state has preference over component's error
      // Because errorProp might be a function, we first need to check if prop is a bool and only then assign its value

      var error = emptyError || errorState || typeof errorProp === 'boolean' && errorProp;
      var fieldId = inputId ? inputId : cuid();
      var fontSize = typescale === 'VDS' ? 'large' : 'medium';
      var body = bodyConfig.getStyles(typescale, 'desktop', fontSize);
      var renderIcon = renderErrorIcon && (error || success) && !focused && !(readOnly || locked) && !disabled;
      var showPassword = this.state.value && this.state.value.length > 0 && mask === 'toggle';
      var renderInlineAction = (type === 'inlineAction' ||
      /* type === 'taxID' || */
      type === 'password' && showPassword) && !(readOnly || locked);
      var labelId = fieldId + ' label';
      var trueLabel = label ? label : '';

      function _dateAriaLabel() {
        if (type === 'date') {
          switch (dateFormat) {
            case 'mmyy':
              return label + ' input, please enter two digits for the month and two digits for the year';

            case 'mmddyy':
              return label + ' input, please enter two digits for the month, two digits for the day and two digits for the year';

            case 'mmddyyyy':
              return label + ' input, please enter two digits for the month, two digits for the day and four digits for the year';
          }
        }
      }

      var accessLabel = ariaLabel ? ariaLabel : type === 'date' ? _dateAriaLabel() : !required ? trueLabel + ' Optional Input Field' : trueLabel + ' Required Input Field';
      var errorText = emptyError ? "You must enter a ".concat(trueLabel) : errorTextProp;
      error && !errorText && console.error('Error Text for Input MUST be provided');

      function _determineAriaLabel() {
        if (error && errorText) {
          if (helperText) {
            return "".concat(accessLabel, " ").concat(errorText, " ").concat(helperText);
          } else {
            return "".concat(accessLabel, " ").concat(errorText);
          }
        } else {
          if (helperText) {
            return "".concat(accessLabel, " ").concat(helperText);
          } else {
            return accessLabel;
          }
        }
      }

      return /*#__PURE__*/React.createElement(ComponentContainer, {
        helperTextPlacement: helperTextPlacement,
        minWidth: minWidth,
        maxWidth: maxWidth,
        containerWidth: width,
        className: className
      }, /*#__PURE__*/React.createElement(StyledContainer, {
        id: id,
        "data-testid": "test-container",
        helperTextPlacement: helperTextPlacement,
        helperText: helperText
      }, label && /*#__PURE__*/React.createElement(Label, (_React$createElement = {
        typescale: typescale,
        Body: Body,
        Micro: Micro,
        width: width,
        disabled: disabled,
        color: color,
        required: required,
        label: label,
        Tooltip: Tooltip,
        tooltipTitle: tooltipTitle,
        tooltipContent: tooltipContent,
        type: type,
        "data-testid": "test-label",
        id: labelId
      }, _defineProperty(_React$createElement, "width", width), _defineProperty(_React$createElement, "aria-label", label), _defineProperty(_React$createElement, "htmlFor", fieldId), _defineProperty(_React$createElement, "cardType", cardBrand), _defineProperty(_React$createElement, "focusState", focusState), _defineProperty(_React$createElement, "labelMarginBottom", labelMarginBottom), _defineProperty(_React$createElement, "overflowEllipsis", overflowEllipsis), _defineProperty(_React$createElement, "helperTextPlacement", helperTextPlacement), _defineProperty(_React$createElement, "helperText", helperText), _defineProperty(_React$createElement, "CreditCardIcon", CreditCardIcon), _defineProperty(_React$createElement, "inverted", inverted), _React$createElement)), /*#__PURE__*/React.createElement(FlexedRowContainer, null, /*#__PURE__*/React.createElement(FlexedColContainer, {
        helperText: helperText,
        helperTextPlacement: helperTextPlacement
      }, renderInputIcon && renderInputIcon(), /*#__PURE__*/React.createElement(InputContainer, {
        readOnlyBorders: readOnlyBorders,
        minWidth: minWidth,
        maxWidth: maxWidth,
        color: color,
        error: error,
        disabled: disabled,
        isOpen: isOpen,
        hoverState: hoverState,
        type: type,
        inputHeight: inputHeight,
        success: success,
        helperText: helperText,
        helperTextPlacement: helperTextPlacement,
        successText: successText,
        errorText: errorText,
        label: label,
        readOnly: locked || readOnly,
        locked: locked || readOnly,
        inverted: inverted,
        onFocus: this._onFocus,
        onBlur: this._onBlur,
        focused: this.state.focused
      }, type === 'creditCard' && /*#__PURE__*/React.createElement(CardIconContainer, {
        error: error,
        cardType: cardType,
        success: success
      }, /*#__PURE__*/React.createElement(CreditCardIcon, {
        inverted: inverted,
        name: cardType
      })), /*#__PURE__*/React.createElement(InputWrapper, null, /*#__PURE__*/React.createElement(StyledInput, _extends({
        maxLength: determineMaxLength(this.state.cardType, type, dateFormat, taxIDFormat, this.state.hidden, cardBrand, maxLength),
        "data-testid": "test-input",
        "data-truevalue": (type === 'creditCard' || type === 'tel') && this.state.value ? this.state.value.replace(/\D/g, '') : '',
        placeholder: placeholder,
        body: body,
        color: color,
        "aria-label": _determineAriaLabel(),
        id: fieldId,
        autocomplete: "off",
        inputHeight: inputHeight
      }, rest, {
        disabled: disabled,
        error: error,
        locked: locked,
        trueType: type,
        readOnlyBorders: readOnlyBorders,
        required: false,
        readOnly: readOnly || locked,
        inverted: inverted,
        onChange: this._onChange,
        value: type === 'password' ? this.props.value : this._determineValue(),
        masked: this._determineInputType() === 'password' && this.state.hidden,
        type: this._determineInputType(),
        calculateInputPadding: calculateInputPadding,
        caretColor: caretColor
      })), type === 'date' && this._renderStickyPlaceholder(body)), (renderIcon || renderInlineAction) && /*#__PURE__*/React.createElement(ActionWrapper, {
        error: error,
        success: success
      }, renderIcon && this._renderIcon(error), renderInlineAction && this._renderInlineAction())), (error && errorText || success && successText) && !disabled && !readOnly && /*#__PURE__*/React.createElement(ErrorText, {
        errorLabelSpacing: errorLabelSpacing,
        bodyConfig: bodyConfig,
        typescale: typescale,
        errorText: errorText,
        error: error,
        success: success,
        successText: successText,
        inverted: inverted,
        color: color
      })), helperText && helperTextPlacement === 'right' && this._renderHelperText()), helperText && helperTextPlacement === 'bottom' && this._renderHelperText()));
    }
  }]);

  return InputField;
}(React.Component);

InputField.defaultProps = defaultProps;
InputField.propTypes = propTypes;
/**
 * @component
 */

export default withVDSManager(InputField);